import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'ui/i18n';
import { action, observable } from 'mobx';
import { authorize } from 'utils/auth';
import { getTotalCount, getIndex } from 'utils/helpers';
import CartState from 'state/Cart';

import {
	render_amount,
	render_drop_ship_address,
	render_est_ship_date,
	render_remove,
	render_editable_number,
	render_cart_item,
	render_editable_holdInvWeeks,
} from 'utils';

import {
	PageContainer,
	GoBack,
	DataTable,
	ChooseColor,
	InfoBox,
	SelfClosingPopup,
	SubmitWithoutUpdatesModal,
	SubmitWithSuccessModal,
	Loader,
	NavigationPrompt,
} from 'ui/components';

import './Cart.scss';

function getColumns(
	items,
	removeItemHandler,
	onChangeQuantityHandler,
	onChangeInvoiceHandler,
	toggleChooseColor,
	onBlurHandler
) {
	return [
		{
			prop: 'number',
			title: <FormattedMessage id="itemsTable.cartSoloItemColumn" />,
			render(row) {
				return render_cart_item(row, toggleChooseColor);
			},
			footer: <FormattedMessage id="cart.footerTotals" />,
		},
		{
			prop: 'quantity_ordered',
			title: <FormattedMessage id="itemsTable.qtyColumn" />,
			align: 'center',
		},
		{
			prop: 'promo_price',
			title: <FormattedMessage id="itemsTable.cartPromoColumn" />,
			align: 'right',
			render(row) {
				return render_amount(row[this.prop], row);
			},
			footer: (
				<span className="noWrap">
					{render_amount(CartState.total_promo_price)}
				</span>
			),
		},
		{
			prop: 'ext_price',
			title: <FormattedMessage id="itemsTable.extColumn" />,
			align: 'right',
			render(row) {
				return render_amount(row[this.prop], row);
			},
			footer: (
				<span className="noWrap">
					{render_amount(CartState.total_ext_price)}
				</span>
			),
		},
		{
			prop: 'invoice_hold_weeks',
			title: <FormattedMessage id="itemsTable.numberHoldInvWksColumn" />,
			align: 'center',
			render(row) {
				return render_editable_holdInvWeeks(row);
			},
		},
		{
			prop: 'est_ship_date_for_cart',
			title: <FormattedMessage id="itemsTable.cartShipDateColumn" />,
			align: 'center',
			render(row) {
				return render_est_ship_date(row);
			},
		},
		{
			prop: 'drop_ship_address',
			title: <FormattedMessage id="itemsTable.cartDropShipColumn" />,
			align: 'center',
			render(row) {
				return render_drop_ship_address(row);
			},
		},
		{
			prop: 'cart_quantity',
			title: <FormattedMessage id="itemsTable.cartQty" />,
			align: 'center',
			render(row) {
				return render_editable_number(
					row,
					row.quantity_ordered,
					true,
					onChangeQuantityHandler,
					onBlurHandler
				);
			},
			footer: (
				<span>
					{getTotalCount(items, 'quantity_ordered')}
					<FormattedMessage id="cart.footerItemsText" />
				</span>
			),
		},
		{
			prop: 'id',
			title: <FormattedMessage id="itemsTable.removeColumn" />,
			align: 'center',
			render(row) {
				return render_remove(row, items, removeItemHandler);
			},
		},
	];
}

@authorize({ rights: ['AddToCart'] })
@inject('OrdersState', 'AppState', 'CartState', 'CartItemsState')
@observer
export default class CartPage extends Component {
	static propTypes = {
		AppState: PropTypes.object.isRequired,
		OrdersState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		history: PropTypes.object.isRequired,
	};

	componentWillUnmount() {
		if (
			this.CartItemsState.cartHasBeenUpdated ||
			this.CartItemsState.hasError
		) {
			this.CartState.deleteAllUpdates();
			this.CartItemsState.emptyCurrentCart();
		}

		this.CartState.removeAPIError();
	}

	CartState = this.props.CartState;
	CartItemsState = this.props.CartItemsState;
	history = this.props.history;
	@observable showModal = false;
	@observable showSubmitWithUpdates = false;
	@observable showSubmitWithSuccess = false;

	updateCart() {
		this.CartState.updateCart(this.CartItemsState.cartUpdates).then(() => {
			this.CartItemsState.emptyCurrentCart();
		});
	}

	submitOrder() {
		if (this.CartItemsState.isCurrentCartEmpty) {
			this.CartState.submitOrder().then(() => {
				if (this.CartState.isSuccessfullySubmited) {
					this.CartItemsState.emptyCurrentCart();
					this.toggleSubmitWithSuccess();
				}
			});
		} else {
			this.toggleSubmitWhenUpdates();
		}
	}

	onBlurHandler(item) {
		const index = getIndex(
			this.CartItemsState.cartUpdates,
			'number',
			item.number
		);

		if (index >= 0 && item.is_configurable && item.quantity_ordered) {
			this.toggleChooseColor(item);
		}
	}

	@action
	removeItem(index, item) {
		item.quantity_ordered = 0;
		item.errorMsgId = null;

		if (index >= 0) {
			this.CartState.cartItems.splice(index, 1);
		}

		const sameitemsFromCart = this.CartState.getItemsFromCart(item);

		if (sameitemsFromCart && sameitemsFromCart.length > 0) {
			sameitemsFromCart.forEach(item =>
				this.CartItemsState.getErrorMessageId(
					true,
					CartState.getAvailableQuantity(item, true),
					CartState.getTotalQuantityFromCart(item),
					item
				)
			);
		}

		this.CartItemsState.addUpdate(item, 0, 'quantity_ordered');
	}

	@action
	changeQuantity(value, item) {
		const isSame = value === item.quantity_ordered;

		item.quantity_ordered = value;
		const itemsFromCart = this.CartState.getItemsFromCart(item);
		const errorMsgIds = this.CartItemsState.getErrorMessageId(
			true,
			this.CartState.getAvailableQuantity(item, true),
			this.CartState.getTotalQuantityFromCart(item),
			item
		);

		if (itemsFromCart.length > 0) {
			itemsFromCart.forEach(item => (item.errorMsgId = errorMsgIds));
		}

		if (!isSame && value !== item.quantity_in_cart) {
			this.CartItemsState.addUpdate(item, value, 'quantity_ordered');
			this.CartState.cartItems.push(
				this.CartState.cartItems[this.CartState.cartItems.length - 1]
			);
			const lastItemindex = -1;
			this.CartState.cartItems.splice(lastItemindex, 1);
		} else {
			this.CartItemsState.deleteUpdate(item, 'quantity_ordered');
		}
	}

	@action
	changeInvoiceWeeks(value, item) {
		item.invoice_hold_weeks = value;
	}

	@action
	toggleChooseColor(itemDetails) {
		this.itemDetails = itemDetails;
		this.showModal = !this.showModal;
	}

	@action
	toggleSubmitWhenUpdates() {
		this.showSubmitWithUpdates = !this.showSubmitWithUpdates;
	}

	@action
	toggleSubmitWithSuccess() {
		this.showSubmitWithSuccess = !this.showSubmitWithSuccess;
	}

	@action
	togglePopup() {
		this.CartState.cartIsUpdated = false;
	}

	render() {
		const { submitOrder } = this.CartState;
		return (
			<div className="Cart">
				{submitOrder.handle({
					loading: () => <Loader cart={true} />,
					resolved: () =>
						this.showSubmitWithSuccess &&
						<SubmitWithSuccessModal
							show={this.showSubmitWithSuccess}
							onRequestClose={this.toggleSubmitWithSuccess.bind(this)}
						/>,
				})}

				<PageContainer
					headerText={
						<span>
							<FormattedMessage id="cart.headerText" />
						</span>
					}
				>
					<div className="Cart-content">
						{(this.props.CartState.errorId ||
							this.CartState.updateCart.state.rejected) &&
							<InfoBox type="danger">
								<FormattedMessage id={this.props.CartState.errorId} />
							</InfoBox>}

						<NavigationPrompt
							when={
								this.CartItemsState.cartHasBeenUpdated ||
								this.CartItemsState.hasError
							}
						/>

						<div className="row Cart-content-header">
							<GoBack
								id="Cart-content-header-goBack"
								goBackText="general.continueShopping"
								history={this.history}
							/>

							<div className="col-md-6 right-side">
								<p id="Cart-content-header-summary" className="pull-right">
									<FormattedMessage id="cart.cartSummary" />
									{this.CartState.cartItems.length}
									<FormattedMessage id="cart.itemsIn" />
								</p>
							</div>
						</div>

						<div className="row">
							<div className="col-md-6">
								<FormattedMessage id="cart.dropShip" />
							</div>

							<div className="col-md-6">
								<button
									id="Cart-content-button-submit"
									className="btn btn-primary pull-right"
									disabled={
										this.CartState.isCartEmpty ||
										this.CartItemsState.hasError ||
										this.CartState.hasError
									}
									onClick={this.submitOrder.bind(this)}
								>
									<FormattedMessage id="cart.submitOrder" />
								</button>

								<button
									id="Cart-content-button-update"
									className="btn btn-primary pull-right"
									disabled={
										!this.CartItemsState.cartHasBeenUpdated ||
										this.CartItemsState.hasError ||
										this.CartState.hasError ||
										this.CartState.updateCart.state.loading
									}
									onClick={this.updateCart.bind(this)}
								>
									<FormattedMessage id="cart.updateCart" />
								</button>
							</div>
						</div>

						<div className="Cart-content-error-handling">
							<SelfClosingPopup
								show={this.CartState.cartIsUpdated}
								content={
									<span>
										<FormattedMessage id="cart.cartUpdatedMessage" />
									</span>
								}
								fullScreen={true}
								onRequestClose={this.togglePopup.bind(this)}
							/>
						</div>

						{this.CartState.isCartEmpty
							? <InfoBox type="info">
									<FormattedMessage id="cart.noItemsAddedMessage" />
								</InfoBox>
							: <div className="Cart-content-products">
									<DataTable
										id="Cart-content-products-table"
										columns={getColumns(
											this.CartState.cartItems,
											this.removeItem.bind(this),
											this.changeQuantity.bind(this),
											this.changeInvoiceWeeks.bind(this),
											this.toggleChooseColor.bind(this),
											this.onBlurHandler.bind(this)
										)}
										rows={this.CartState.cartItems}
										hasFooter={true}
										hasStickyHeader={true}
									/>

									{this.showModal &&
										<ChooseColor
											show={this.showModal}
											onRequestClose={this.toggleChooseColor.bind(this)}
											item={this.itemDetails}
											inEditMode={true}
										/>}

									{this.showSubmitWithUpdates &&
										<SubmitWithoutUpdatesModal
											show={this.showSubmitWithUpdates}
											onRequestClose={this.toggleSubmitWhenUpdates.bind(this)}
											onSubmit={this.submitOrder.bind(this)}
										/>}
								</div>}
					</div>
				</PageContainer>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/Cart/index.js